/**
 * Commande ouverte par le QR du ticket client : `/shop/c/<jeton signé>` (20/10/2026).
 *
 * Le ticket qui accompagne une commande (livraison ou retrait) portait le QR
 * du livreur. Il porte désormais celui du CLIENT, vers sa commande, même
 * s'il n'a pas de compte (commande préparée en caisse). La page ne montre que
 * le contenu : ni adresse, ni téléphone, ni email — le prénom seulement.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ecommerceApi, type CommandePublique } from '../../api/ecommerce.api';
import { useTitrePage } from '../../hooks/useTitrePage';

const STATUTS: Record<string, string> = {
  pending: 'Enregistrée',
  confirmed: 'Confirmée',
  preparing: 'En préparation',
  ready: 'Prête',
  delivering: 'En livraison',
  completed: 'Remise',
  cancelled: 'Annulée',
};

const euros = (c: number) => (c / 100).toFixed(2).replace('.', ',') + ' €';

function quantite(l: CommandePublique['lignes'][number]): string {
  if (l.unite === 'grams') return `${Math.round(l.quantite)} g`;
  const q = Number.isInteger(l.quantite) ? String(l.quantite) : l.quantite.toFixed(3).replace(/0+$/, '').replace('.', ',');
  return l.unite === 'kg' ? `${q} kg` : `× ${q}`;
}

export default function CommandePubliquePage() {
  const { jeton = '' } = useParams<{ jeton: string }>();
  const [commande, setCommande] = useState<CommandePublique | null>(null);
  const [introuvable, setIntrouvable] = useState(false);
  useTitrePage(commande ? `Commande ${commande.order_number}` : 'Votre commande');

  useEffect(() => {
    let actif = true;
    ecommerceApi.shop
      .commandePublique(jeton)
      .then((c) => actif && setCommande(c))
      .catch(() => actif && setIntrouvable(true));
    return () => {
      actif = false;
    };
  }, [jeton]);

  return (
    <div className="min-h-screen bg-[#faf7f0]">
      <header className="shop-sans bg-white border-b border-black/[.08]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/shop" className="flex items-center gap-2 text-[#3a3324] font-semibold hover:text-green-800">
            <ArrowLeft className="w-5 h-5" />
            Boutique
          </Link>
          <span className="flex-1 text-center font-bold text-[#3a3324] truncate">Les Folies Maraîchères</span>
        </div>
      </header>

      <main className="shop-sans max-w-2xl mx-auto px-4 py-8">
        {introuvable ? (
          <div className="text-center py-12">
            <p className="text-lg text-gray-700">Cette commande est introuvable. Le lien du ticket est peut-être incomplet.</p>
            <Link to="/shop" className="mt-4 inline-block underline text-green-800 font-semibold">Voir la boutique</Link>
          </div>
        ) : !commande ? (
          <p className="text-center text-gray-600 py-12">Chargement…</p>
        ) : (
          <article className="rounded-2xl bg-white border border-black/[.08] p-5 sm:p-6">
            <p className="text-sm text-gray-600">
              {commande.prenom ? `${commande.prenom}, voici votre commande` : 'Votre commande'}
            </p>
            <h1 className="text-2xl font-bold text-[#3a3324] mt-1">{commande.order_number}</h1>
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <span className="rounded-full bg-green-50 text-green-800 px-3 py-1 font-semibold">
                {STATUTS[commande.status] || commande.status}
              </span>
              {commande.date && (
                <span className="rounded-full bg-gray-100 text-gray-700 px-3 py-1">
                  {commande.livraison ? 'Livraison' : 'Retrait'} le{' '}
                  {new Date(commande.date).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' })}
                </span>
              )}
              <span className={`rounded-full px-3 py-1 ${commande.payee ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
                {commande.payee ? 'Payée' : 'À régler'}
              </span>
            </div>

            <ul className="mt-5 divide-y divide-black/[.06]">
              {commande.lignes.map((l, i) => (
                <li key={i} className="py-2.5 flex items-baseline gap-3">
                  <span className="flex-1 min-w-0 text-[#3a3324]">{l.nom}</span>
                  <span className="text-sm text-gray-600 tabular-nums">{quantite(l)}</span>
                  <span className="w-20 text-right font-semibold tabular-nums text-[#3a3324]">{euros(l.total_cents)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t-2 border-[#2d6a4f] flex items-baseline justify-between">
              <span className="font-bold text-[#3a3324]">Total</span>
              <span className="text-xl font-bold tabular-nums text-[#3a3324]">{euros(commande.total_cents)}</span>
            </div>
            <p className="mt-6 text-sm text-gray-600">
              Une question sur votre commande ? Répondez à notre email ou passez nous voir au magasin.
            </p>
          </article>
        )}
      </main>
    </div>
  );
}
